import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {IconButton, Tooltip} from '@mui/material';
import {useTranslation} from 'react-i18next';
import RefreshIcon from '@mui/icons-material/Refresh';
import {useSelector} from "react-redux";
import {RootState} from "../../store";

interface ExtensionStatusProps {
    variant?: 'active' | 'simple';
    className?: string;
}

// Triggers background extension ping with provided Steam IDs.
export const triggerExtensionPing = (steamIds: string[]): void => {
    window.postMessage({
        target: 'MOONSKINS_EXTENSION',
        version: 1,
        request_type: 10,
        payload: {
            steamIds: steamIds
        },
        id: Math.floor(Math.random() * 1000000)
    }, '*');
};

const ExtensionStatus: React.FC<ExtensionStatusProps> = ({variant = 'active', className = ''}) => {
    const {t} = useTranslation();
    const [isExtensionInstalled, setIsExtensionInstalled] = useState(false);
    const [timeLeft, setTimeLeft] = useState(180);
    const [manualCooldown, setManualCooldown] = useState(0);
    const timerRef = useRef<NodeJS.Timeout | null>(null);
    const steamAccounts = useSelector((state: RootState) => state.auth.steamAccounts);
    const steamIds = useMemo(() => steamAccounts.map((acc) => acc.steamId), [steamAccounts]);

    const triggerPing = useCallback(() => {
        triggerExtensionPing(steamIds);
    }, [steamIds]);

    const triggerPingRef = useRef(triggerPing);
    useEffect(() => {
        triggerPingRef.current = triggerPing;
    }, [triggerPing]);

    useEffect(() => {
        if (variant !== 'active') return;
        const savedExpiry = localStorage.getItem('moonskins_ping_expiry');
        const now = Math.floor(Date.now() / 1000);
        let initialTime = 180;

        if (savedExpiry) {
            const remaining = parseInt(savedExpiry) - now;
            initialTime = remaining > 0 ? remaining : 180;
        }
        setTimeLeft(initialTime);

        timerRef.current = setInterval(() => {
            setTimeLeft((prev) => {
                if (prev <= 1) {
                    triggerPingRef.current();
                    const nextExpiry = Math.floor(Date.now() / 1000) + 180;
                    localStorage.setItem('moonskins_ping_expiry', nextExpiry.toString());
                    return 180;
                }
                return prev - 1;
            });
        }, 1000);
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, [variant]);

    useEffect(() => {
        const checkExtension = () => {
            if (globalThis.MOONSKINS_EXTENSION_ENABLED === true) setIsExtensionInstalled(true);
        };
        checkExtension();
        globalThis.addEventListener('MOONSKINS_EXTENSION_INSTALLED', checkExtension);

        const checkCooldown = () => {
            const now = Date.now();
            const lastPingStr = localStorage.getItem('moonskins_last_manual_ping');
            const lastPing = lastPingStr ? parseInt(lastPingStr) : 0;
            const secondsSinceLast = Math.floor((now - lastPing) / 1000);
            setManualCooldown(secondsSinceLast < 30 ? 30 - secondsSinceLast : 0);
        };

        const cdInterval = setInterval(checkCooldown, 1000);
        return () => {
            clearInterval(cdInterval);
            globalThis.removeEventListener('MOONSKINS_EXTENSION_INSTALLED', checkCooldown);
        };
    }, []);

    const handleManualPing = () => {
        if (manualCooldown > 0) return;
        localStorage.setItem('moonskins_last_manual_ping', Date.now().toString());
        triggerPing();
    };

    const formatTime = (seconds: number) => {
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    };

    const userAgent = navigator.userAgent.toLowerCase();
    const isFirefox = userAgent.includes('firefox');
    const isChrome = userAgent.includes('chrome') && !userAgent.includes('edg') && !userAgent.includes('opr');

    if (!isExtensionInstalled) {
        return (
            <div
                className={`bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-gray-700/50 rounded-xl h-11 ${className}`}>
                <div className="flex items-center h-full px-3 gap-2">
                    <div className="flex-1 min-w-0">
                        <div className="flex flex-col">
                            <span
                                className="text-[11px] font-bold text-gray-800 dark:text-gray-100 leading-none uppercase tracking-tighter">Extension</span>
                            <span
                                className="text-[9px] text-gray-500 dark:text-gray-400 font-semibold mt-0.5 leading-none uppercase">Install Required</span>
                        </div>
                    </div>
                    <div className="flex gap-1.5 shrink-0">
                        {(isChrome || (!isChrome && !isFirefox)) && (
                            <a href="#" style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '28px',
                                height: '28px',
                                flexShrink: 0
                            }}
                               className="bg-white dark:bg-neutral-800 border border-gray-200 dark:border-white/10 rounded-lg hover:bg-gray-50 dark:hover:bg-neutral-700">
                                <img
                                    src="https://upload.wikimedia.org/wikipedia/commons/e/e1/Google_Chrome_icon_%28February_2022%29.svg"
                                    style={{width: '14px', height: '14px'}} alt="chrome"/>
                            </a>
                        )}
                        {(isFirefox || (!isChrome && !isFirefox)) && (
                            <a href="#" style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '28px',
                                height: '28px',
                                flexShrink: 0
                            }}
                               className="bg-white dark:bg-neutral-800 border border-gray-200 dark:border-white/10 rounded-lg hover:bg-gray-50 dark:hover:bg-neutral-700">
                                <img src="https://upload.wikimedia.org/wikipedia/commons/a/a0/Firefox_logo%2C_2019.svg"
                                     style={{width: '14px', height: '14px'}} alt="firefox"/>
                            </a>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={`flex items-center gap-2 w-full ${className}`}>
            {variant === 'active' && (
                <div
                    className="flex items-center justify-between bg-blue-50/40 dark:bg-white/[0.03] border border-blue-100 dark:border-white/5 px-3 rounded-xl h-11 flex-1">
                    <Tooltip title={t('history.tooltips.auto_sync')} arrow>
                        <div className="flex flex-col items-start justify-center h-full">
                            <div className="!text-[9px] text-blue-500 dark:text-gray-500 !font-extrabold uppercase !leading-none mb-0.5">Auto-Ping</div>
                            <div className="text-[12px] font-bold text-blue-600 dark:text-gray-200 leading-none">{formatTime(timeLeft)}</div>
                        </div>
                    </Tooltip>
                    <div className="flex items-center gap-1 h-full">
                        <div className="h-4 w-[1px] bg-blue-200 dark:bg-white/10 mx-1"/>
                        <Tooltip title={manualCooldown > 0 ? `Wait (${manualCooldown}s)` : 'Sync'} arrow>
              <span>
                <IconButton onClick={handleManualPing} disabled={manualCooldown > 0} size="small"
                            className={`!p-1 ${manualCooldown > 0 ? 'opacity-30' : 'text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-white/5'}`}>
                  <RefreshIcon sx={{fontSize: 16}}
                               className={manualCooldown === 0 ? 'hover:rotate-180 transition-transform duration-500' : ''}/>
                </IconButton>
              </span>
                        </Tooltip>
                    </div>
                </div>
            )}

            <div
                className={`flex items-center justify-center gap-2 px-3 rounded-xl border border-green-100 bg-green-50/40 dark:border-green-500/20 dark:bg-green-500/5 h-11 ${variant === 'active' ? 'flex-none' : 'flex-1'}`}>
                <div className="relative flex w-1.5 h-1.5 shrink-0">
                    <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-ping absolute opacity-75"/>
                    <div className="w-1.5 h-1.5 bg-green-500 rounded-full relative"/>
                </div>

                <div className="flex items-center gap-1.5">
                    {/* Browser icon only on mobile (hidden on sm and up) */}
                    <div className="flex sm:hidden items-center">
                        {(isChrome || (!isChrome && !isFirefox)) && (
                            <img
                                src="https://upload.wikimedia.org/wikipedia/commons/e/e1/Google_Chrome_icon_%28February_2022%29.svg"
                                className="w-3.5 h-3.5 shrink-0" alt="chrome"/>
                        )}
                        {isFirefox && (
                            <img src="https://upload.wikimedia.org/wikipedia/commons/a/a0/Firefox_logo%2C_2019.svg"
                                 className="w-3.5 h-3.5 shrink-0" alt="firefox"/>
                        )}
                    </div>

                    <div
                        className="text-[10px] font-bold uppercase tracking-wider text-green-600 dark:text-green-400 whitespace-nowrap leading-none mt-[1px]">
                        <span className="hidden sm:inline">EXTENSION LIVE</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ExtensionStatus;